import React, { useEffect, useState } from "react";
import { Palette, Loader2, ScanLine } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePoll } from "@/hooks/usePoll";
import NoProject from "@/components/NoProject";
import { PageHeader, ToggleRow, Slider } from "@/components/finishing/Controls";
import { BeforeAfter } from "@/components/finishing/BeforeAfter";
import { CameraCard } from "@/components/finishing/CameraCard";
import { PresetGrid } from "@/components/finishing/PresetGrid";
import { SceneMatchCard } from "@/components/finishing/SceneMatchCard";
import { LookSnapshots } from "@/components/finishing/LookSnapshots";
import { ClientApproval } from "@/components/finishing/ClientApproval";

export default function ColorGrade() {
  const { projectId } = useApp();
  const { data, setData } = usePoll(() => (projectId ? api.getColor(projectId) : Promise.resolve(null)), 3000, [projectId]);
  const [presets, setPresets] = useState([]);
  const [intensity, setIntensity] = useState(null);
  const [busy, setBusy] = useState(false);
  const [clip, setClip] = useState("");
  const [snaps, setSnaps] = useState([]);
  const [scenes, setScenes] = useState(null);
  const [approvals, setApprovals] = useState([]);
  useEffect(() => { api.colorPresets().then(setPresets).catch(() => {}); }, []);
  const loadExtras = () => {
    if (!projectId) return;
    api.snapshots(projectId).then(setSnaps).catch(() => {});
    api.colorScenes(projectId).then(setScenes).catch(() => {});
    api.approvals(projectId).then(setApprovals).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(loadExtras, [projectId, data?.updated_at, data?.active_snapshot_id]);

  if (!projectId) return <NoProject title="Color needs a project" />;
  if (!data) return <div className="p-8"><Loader2 className="animate-spin text-[#E6B04E]" /></div>;

  const save = async (patch) => {
    try { setData(await api.updateColor(projectId, patch)); toast.success("Grade updated — applies on next render"); loadExtras(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const analyze = async () => {
    setBusy(true);
    try { await api.analyzeColor(projectId, data.analyzed_clips > 0); toast.success("Color analysis queued"); }
    catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  const tweak = async (cid, m) => {
    try { setData(await api.tweakCamera(projectId, cid, m)); } catch { toast.error("Failed"); }
  };

  const job = data.job;
  const running = job && ["queued", "running"].includes(job.status);
  const cams = Object.values(data.cameras || {});
  const ref = (data.references || []).find((r) => r.id === data.reference_id);
  const inten = intensity ?? data.intensity;
  const previewId = clip || data.sample_media_id;

  return (
    <div className="p-8 fade-up space-y-6">
      <PageHeader icon={Palette} title="Color Grade Studio"
        subtitle="Exposure & white-balance correction · camera matching · reference-driven creative grade — applied at render">
        <button className="btn btn-gold" onClick={analyze} disabled={busy || running} data-testid="btn-analyze-color">
          {busy || running ? <Loader2 size={16} className="animate-spin" /> : <ScanLine size={16} />}
          {running ? `Analyzing ${job.progress || 0}%` : data.analyzed_clips ? "Re-analyze" : "Analyze Color"}
        </button>
      </PageHeader>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
        <div className="space-y-3">
          <BeforeAfter mediaId={previewId} version={data.updated_at || "0"} />
          <div className="flex items-center justify-between gap-3">
            <p className="metric-label" data-testid="color-analyzed-count">{data.analyzed_clips}/{data.total_clips} clips measured · {cams.length} camera(s)</p>
            <select className="studio-input !py-1 text-xs max-w-[260px]" value={previewId || ""} onChange={(e) => setClip(e.target.value)} data-testid="color-preview-clip">
              {(data.preview_clips || []).map((c) => <option key={c.id} value={c.id}>{c.filename}</option>)}
            </select>
          </div>
        </div>
        <div className="studio-card p-5 space-y-5">
          <ToggleRow label="Apply grade at render" desc="Master switch for every color stage" on={data.enabled}
            onClick={() => save({ enabled: !data.enabled })} testid="color-enabled-toggle" />
          <ToggleRow label="Auto technical correction" desc="Per-clip exposure + gray-world white balance" on={data.auto_correct}
            onClick={() => save({ auto_correct: !data.auto_correct })} testid="color-autocorrect-toggle" />
          <div>
            <label className="metric-label block mb-1">Reference video (for Match Reference)</label>
            <select className="studio-input !py-1.5 text-sm" value={data.reference_id || ""} data-testid="color-reference-select"
              onChange={(e) => save({ reference_id: e.target.value })}>
              <option value="">— none —</option>
              {(data.references || []).map((r) => <option key={r.id} value={r.id} disabled={!r.has_color}>{`${r.name}${r.has_color ? "" : " (re-analyze)"}`}</option>)}
            </select>
          </div>
          <Slider label="Creative intensity" value={inten} min={0} max={1} testid="color-intensity"
            fmt={(v) => `${Math.round(v * 100)}%`} onChange={setIntensity}
            onCommit={(v) => { setIntensity(null); save({ intensity: v }); }} />
        </div>
      </div>

      <div className="space-y-3">
        <h2 className="font-head text-lg font-bold">Creative Look</h2>
        <PresetGrid presets={presets} value={data.creative_preset} hasReference={!!ref?.has_color}
          onPick={(id) => save({ creative_preset: id })} />
      </div>

      <div className="space-y-3">
        <h2 className="font-head text-lg font-bold">Look Snapshots</h2>
        <LookSnapshots projectId={projectId} snaps={snaps} mediaId={previewId} onChange={() => { loadExtras(); api.getColor(projectId).then(setData); }} />
      </div>

      <div className="space-y-3">
        <h2 className="font-head text-lg font-bold">Client Look Approval</h2>
        <ClientApproval projectId={projectId} snaps={snaps} clips={data.preview_clips || []} approvals={approvals}
          onChange={() => { loadExtras(); api.getColor(projectId).then(setData); }} />
      </div>

      <SceneMatchCard value={data.scene_match} scenes={scenes} onSave={(sm) => save({ scene_match: sm })} />

      <div className="space-y-3">
        <h2 className="font-head text-lg font-bold">Camera Matching</h2>
        {cams.length === 0 ? (
          <div className="studio-card p-6 text-sm text-[#64748B]" data-testid="color-no-cameras">Run color analysis to measure each camera and match it to the hero camera.</div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {cams.map((c) => (
              <CameraCard key={c.id} cam={c} isHero={c.id === data.hero_camera}
                onHero={() => save({ hero_camera: c.id })} onTweak={(m) => tweak(c.id, m)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
